import type { AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { type FormEvent, useState } from "react";
import { ApiError, api } from "../api/client.js";
import oasisLogoDark from "../assets/brand/oasis-logo-dark.png";
import oasisLogoLight from "../assets/brand/oasis-logo-light.png";
import { ThemeToggle } from "../components/ThemeToggle.js";
import { type Language, useTranslation } from "../i18n.js";

/**
 * Password and TOTP code in one step, because that is what the API accepts (D-007).
 *
 * A two-step form would need the server to hand out something that proves "the password was right",
 * and that something is a smaller, shorter-lived thing to steal than a password — but a thing to
 * steal nonetheless, existing only to make the form feel familiar.
 */
export function Login({
  language,
  onLanguageChange,
  onSignedIn,
  onThemeChange,
  theme,
}: {
  language: Language;
  onLanguageChange: (language: Language) => void;
  onSignedIn: (admin: AuthenticatedAdmin) => void;
  onThemeChange: (theme: "dark" | "light") => void;
  theme: "dark" | "light";
}) {
  const t = useTranslation();
  const bypassTotp = import.meta.env.VITE_ADMIN_TOTP_BYPASS === "true";
  const [code, setCode] = useState(bypassTotp ? "000000" : "");
  const [email, setEmail] = useState(
    bypassTotp ? (import.meta.env.VITE_ADMIN_TEST_EMAIL ?? "") : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setPending(true);

    try {
      const { admin } = await api.login({ code, email, password });
      onSignedIn(admin);
    } catch (caught) {
      // The server does not say whether the password or the code was wrong, and neither does this:
      // repeating a distinction the API refuses to make would hand it back to whoever is guessing.
      setError(caught instanceof ApiError ? caught.message : t("Could not sign in."));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="signin">
      <form className="signin-card" onSubmit={submit}>
        <div className="signin-controls">
          <ThemeToggle
            dark={theme === "dark"}
            onToggle={() => onThemeChange(theme === "dark" ? "light" : "dark")}
          />
          <label className="language-picker">
            <span className="visually-hidden">{language === "fr" ? "Langue" : "Language"}</span>
            <select
              aria-label={language === "fr" ? "Langue" : "Language"}
              onChange={(event) => onLanguageChange(event.target.value as Language)}
              value={language}
            >
              <option value="en">EN</option>
              <option value="fr">FR</option>
            </select>
          </label>
        </div>
        <div className="signin-brand">
          <span className="signin-brand-logos">
            <img alt="Oasis" className="signin-brand-logo brand-for-light" src={oasisLogoDark} />
            <img alt="Oasis" className="signin-brand-logo brand-for-dark" src={oasisLogoLight} />
          </span>
        </div>

        <div className="field">
          <label htmlFor="email">{t("Email")}</label>
          <input
            autoComplete="username"
            className="input"
            id="email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
        </div>

        <div className="field">
          <label htmlFor="password">{t("Password")}</label>
          <div className="password-field">
            <input
              autoComplete="current-password"
              className="input"
              id="password"
              onChange={(event) => setPassword(event.target.value)}
              required
              type={showPassword ? "text" : "password"}
              value={password}
            />
            <button
              aria-label={t(showPassword ? "Hide password" : "Show password")}
              onClick={() => setShowPassword((visible) => !visible)}
              type="button"
            >
              {t(showPassword ? "Hide" : "Show")}
            </button>
          </div>
        </div>

        {bypassTotp ? null : (
          <div className="field">
            <label htmlFor="code">{t("Authenticator code")}</label>
            <input
              autoComplete="one-time-code"
              className="input mono-12"
              id="code"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => setCode(event.target.value)}
              pattern="[0-9]{6}"
              required
              value={code}
            />
          </div>
        )}

        {error ? (
          <p className="banner" role="alert">
            {error}
          </p>
        ) : null}

        <button className="btn" disabled={pending} type="submit">
          {t(pending ? "Signing in…" : "Sign in")}
        </button>

        <p className="hint">
          {t(
            bypassTotp
              ? "Test environment: authenticator verification is disabled."
              : "Password and code together. There is no registration — the first operator is created from the command line.",
          )}
        </p>
      </form>
    </div>
  );
}
